import React from "react";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";
import Select, { SingleValue } from "react-select";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../../common/hooks/hooks";
import {
  OrderByOptions,
  setSorting,
  setActiveView,
  setPerPage,
} from "../../productSlice";
import Icon from "../../../../../common/components/Icon/Icon";

interface ProductsToolbarProps {
  isLoading: boolean;
  // isFetching is not passed down to avoid layout shift during fetches
  // isError is not passed, error handling is deferred to ProductsGrid component
  // isLoading and success states (with our without data), are handled in the component
}

const sortingOptionValues: OrderByOptions[] = [
  "menu_order",
  "popularity",
  "date",
  "price-asc",
  "price-desc",
];

const perPageOptions = [10, 20, 30];

const ProductsToolbar: React.FC<ProductsToolbarProps> = ({ isLoading }) => {
  const dispatch = useAppDispatch();
  const { t, ready: areTranslationsReady } = useTranslation();

  // Combined loading state
  const isComponentLoading = isLoading || !areTranslationsReady;

  const { totalProducts, currentPage, perPage } = useAppSelector(
    (state) => state.product.pagination
  );
  const currentSort = useAppSelector((state) => state.product.filters.orderby);
  const activeView = useAppSelector((state) => state.product.ui.activeView);

  const startItem = totalProducts > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endItem = Math.min(currentPage * perPage, totalProducts);

  const resultText = t("productsArchive.toolbar.resultsText", {
    startItem,
    endItem,
    totalProducts,
  });

  // --- React Select Integration ---
  const sortOptions = sortingOptionValues.map((value) => ({
    value,
    label: t(`productsArchive.toolbar.sortOptions.${value}`),
  }));

  const selectedOption = sortOptions.find(
    (option) => option.value === currentSort
  );

  const handleSortChange = (
    option: SingleValue<{ value: OrderByOptions; label: string }>
  ) => {
    if (option) {
      dispatch(setSorting(option.value));
    }
  };

  return (
    <div className="products-toolbar">
      {isComponentLoading ? (
        // --- LOADING STATE ---
        <>
          <div className="products-toolbar__left-group">
            <Skeleton height={40} width={220} />
          </div>
          <div className="products-toolbar__results-count">
            <Skeleton width={200} height={20} />
          </div>
          <div className="products-toolbar__right-group">
            <Skeleton height={40} width={120} />
            <Skeleton height={40} width={80} />
          </div>
        </>
      ) : (
        // --- SUCCESS STATE ---
        <>
          <div className="products-toolbar__left-group">
            <div className="products-toolbar__sorting">
              <Select
                instanceId="product-sorting-select"
                value={selectedOption}
                onChange={handleSortChange}
                options={sortOptions}
                className="products-toolbar__select-container"
                classNamePrefix="products-toolbar-select"
                isSearchable={false}
                aria-label={t("productsArchive.toolbar.sortByLabel")}
              />
            </div>
          </div>

          <div className="products-toolbar__results-count">
            <p>
              {totalProducts > 0
                ? resultText
                : t("productsArchive.toolbar.noResultsText")}
            </p>
          </div>

          <div className="products-toolbar__right-group">
            <div className="products-toolbar__per-page-switcher">
              {perPageOptions.map((option) => (
                <button
                  key={option}
                  className={`products-toolbar__per-page-btn ${
                    perPage === option ? "is-active" : ""
                  }`}
                  onClick={() => dispatch(setPerPage(option))}
                  aria-label={t("productsArchive.toolbar.perPageLabel", {
                    count: option,
                  })}
                  aria-pressed={perPage === option}
                >
                  {option}
                </button>
              ))}
            </div>
            <div className="products-toolbar__view-switcher">
              <button
                className={`products-toolbar__view-btn ${
                  activeView === "grid" ? "is-active" : ""
                }`}
                onClick={() => dispatch(setActiveView("grid"))}
                aria-label={t("productsArchive.toolbar.viewAsGrid")}
                aria-pressed={activeView === "grid"}
              >
                <Icon iconName="grid" className="icon--stroke-thicker" />
              </button>
              <button
                className={`products-toolbar__view-btn ${
                  activeView === "list" ? "is-active" : ""
                }`}
                onClick={() => dispatch(setActiveView("list"))}
                aria-label={t("productsArchive.toolbar.viewAsList")}
                aria-pressed={activeView === "list"}
              >
                <Icon iconName="list" className="icon--stroke-thicker" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductsToolbar;
